import { Channel } from 'api/ChannelsAPI';
import { MessageType } from 'api/MessageTypeAPI';
import { getFeatureFlags } from 'core/userContext';
import { useChannelModalsContext } from 'modules/channels/contexts';
import {
  createChannelOverflowOptions,
  createMessageTypeOverflowOptions,
} from 'modules/channels/utils/overflowOptions';
import { EspAccount } from 'modules/graphql/types';
import { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { SearchableText } from 'ui-beta/searchableText';
import { CollapsibleContentCard, CollapsibleContentCardRow } from 'ui/card/collapsibleContentCard';
import { ButtonContainer } from './ChannelCard.styled';
import { ChannelMetadata, MessageTypeMetadata } from './Metadata';

export const TEXT = {
  itemTypeHeading: 'Message types',
  itemTypeName: {
    singular: 'message type',
    plural: 'message types',
  },
};

type Props = {
  channel: Channel;
  customCardAction: ReactNode;
  espAccounts: EspAccount[];
  inputValue: string;
  messageTypes: MessageType[];
};

export const ChannelCard = ({
  channel,
  customCardAction,
  espAccounts,
  inputValue,
  messageTypes,
}: Props) => {
  const {
    showDeleteChannelModal,
    showDeleteMessageTypeModal,
    showIgnoreFrequencyManagement,
    showUpdateChannelModal,
    showUpdateRateLimitModal,
    showUpdateFrequencyCapModal,
    showUpdateMessageTypeModal,
    showUpdateToDoubleOptInModal,
  } = useChannelModalsContext();
  const { name, id } = channel;
  const messageTypeLabel =
    messageTypes?.length > 1 ? TEXT.itemTypeName.plural : TEXT.itemTypeName.singular;
  const channelOptions = createChannelOverflowOptions({
    onUpdate: () => showUpdateChannelModal(channel, espAccounts),
    onDelete: () => showDeleteChannelModal(channel),
  });

  const featureFlags = useSelector(getFeatureFlags);

  return (
    <CollapsibleContentCard
      customCardAction={customCardAction}
      contentHeading={TEXT.itemTypeHeading}
      hasChildren={messageTypes?.length > 0}
      id={`channel-${id}`}
      key={id}
      metadata={
        <ChannelMetadata channel={channel} espAccounts={espAccounts} inputValue={inputValue} />
      }
      cardOverflowOptions={channelOptions}
      title={<SearchableText text={name} searchText={inputValue} isCaseSensitive={false} />}
      toggleLabel={`${messageTypes?.length} ${messageTypeLabel}`}
    >
      <>
        {messageTypes?.map((type: MessageType) => {
          const messageTypeOptions = createMessageTypeOverflowOptions({
            channel,
            messageType: type,
            onDeleteMessageType: () => showDeleteMessageTypeModal(type),
            onIgnoreFrequencyCap: () => showIgnoreFrequencyManagement(type),
            onUpdateRateLimit: () => showUpdateRateLimitModal(type),
            onUpdateFrequencyCap: () => showUpdateFrequencyCapModal(type),
            onUpdateMessageType: () => showUpdateMessageTypeModal(type),
            onUpdateToDoubleOptIn: () => showUpdateToDoubleOptInModal(type),
            featureFlags,
          });
          return (
            <CollapsibleContentCardRow
              key={type.id}
              data-test="message-type-row"
              id={type.id}
              subtitle={<SearchableText searchText={inputValue} text={type.name} />}
              metadata={
                <MessageTypeMetadata
                  channel={channel}
                  inputValue={inputValue}
                  messageType={type}
                  featureFlags={featureFlags}
                />
              }
              options={messageTypeOptions}
            />
          );
        })}
        <ButtonContainer>{customCardAction}</ButtonContainer>
      </>
    </CollapsibleContentCard>
  );
};
